import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useEffect, useLayoutEffect, useRef, useState } from "react"

/** Matches `MarkedPassageIn.quote`'s own display cap (the spec's "cut to
 *  120 chars"), shorter than the 1000-char storage cap in `markSelection.ts`,
 *  this is purely the popover's own quiet header, never sent anywhere. */
const QUOTE_DISPLAY_LENGTH = 120

/** F8 (a11y, review wave 5): the gap kept between the card's own edge and
 *  the viewport's, on whichever side it gets clamped against. */
const VIEWPORT_EDGE_MARGIN = 8

function truncateQuote(quote: string): string {
  if (quote.length <= QUOTE_DISPLAY_LENGTH) return quote
  return `${quote.slice(0, QUOTE_DISPLAY_LENGTH).trimEnd()}…`
}

export interface MarkQuestionPopoverTarget {
  framework: string
  statementId: string
  label: string
}

export interface MarkQuestionPopoverProps {
  /** The mark chip's own capture-time coordinates (`DocumentPane`'s
   *  `onMark` second argument, `markChip.x`/`.y`): fixed-viewport
   *  coordinates, the SAME point the chip itself sat at (`.sv2-selchip`).
   *  The popover replaces the chip in place rather than floating somewhere
   *  unrelated to the selection. */
  anchor: { x: number; y: number }
  quote: string
  targets: MarkQuestionPopoverTarget[]
  framework: string
  statementId: string
  onTargetChange: (framework: string, statementId: string) => void
  question: string
  onQuestionChange: (question: string) => void
  onCancel: () => void
  onConfirm: () => void
  confirmDisabled?: boolean
}

/**
 * TUR-1211 (W5-1): the mark-mode popover replaces the block that used to
 * float, unstyled, over the app's global header (`StudioV2Layout`'s old
 * `pendingMark` JSX, `sv2-markpopover`, a class with no CSS rule behind
 * it, `absolute right-3 top-14`) with an actual Studio V2 card, anchored at
 * the mark chip's own capture-time coordinates instead of a hardcoded
 * corner.
 *
 * `position: fixed` at `anchor` is the SAME mechanism `.sv2-selchip`
 * already uses successfully one component over (see `DocumentPane`); no
 * new containing-block concern to solve here, no portal needed.
 *
 * Deliberately does NOT clear on scroll the way `.sv2-selchip` does: the
 * chip is a transient affordance that dies with the selection, this is a
 * committed form with typed input the reviewer is actively filling in:
 * scrolling the document while it's open leaves it in place rather than
 * discarding a half-written question.
 */
export function MarkQuestionPopover({
  anchor,
  quote,
  targets,
  framework,
  statementId,
  onTargetChange,
  question,
  onQuestionChange,
  onCancel,
  onConfirm,
  confirmDisabled = false,
}: MarkQuestionPopoverProps) {
  const { translate } = useTranslation()
  const rootRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const selectRef = useRef<HTMLSelectElement>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const confirmRef = useRef<HTMLButtonElement>(null)
  // Tab-trap order (F8): the only four focusable elements the card has, in
  // the same order they appear in the DOM.
  // The backend rejects an empty question (`DeclarationQuestionIn.question`,
  // min_length 1), so Hinzufügen stays disabled until there is text: an
  // enabled button on an empty field only ever produced a 422 and a generic
  // error toast (live test, wave 5).
  const addDisabled = confirmDisabled || question.trim() === ""
  const tabOrder = [textareaRef, selectRef, cancelRef, confirmRef]

  // F8 (a11y, review wave 5): the card never overflows the viewport. Its own
  // size isn't known before it renders, so this starts at the raw `anchor`
  // (never worse than the old, unclamped position) and clamps once
  // `rootRef` has a measured box, before the browser paints.
  const [position, setPosition] = useState(anchor)
  useLayoutEffect(() => {
    const rect = rootRef.current?.getBoundingClientRect()
    const width = rect?.width ?? 0
    const height = rect?.height ?? 0
    setPosition({
      x: Math.min(anchor.x, window.innerWidth - width - VIEWPORT_EDGE_MARGIN),
      y: Math.min(anchor.y, window.innerHeight - height - VIEWPORT_EDGE_MARGIN),
    })
  }, [anchor.x, anchor.y])

  // Focus lands on the question field the instant the popover opens: the
  // reviewer just clicked the mark chip with the mouse, no reason to make
  // them click again before typing.
  useEffect(() => {
    textareaRef.current?.focus()
  }, [])

  // Click-away cancels, the same contract as any other popover in the app.
  // A `mousedown` listener, not `click`: the click that OPENED this popover
  // (the mark chip) already fired its own `mousedown` before this effect
  // ran, so the very next `mousedown` anywhere else is a genuinely new
  // interaction, never the opening click re-triggering itself.
  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        onCancel()
      }
    }
    document.addEventListener("mousedown", onMouseDown)
    return () => document.removeEventListener("mousedown", onMouseDown)
  }, [onCancel])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      // `stopPropagation`: without it this bubbles all the way to
      // `useStudioV2Keyboard`'s window-level Escape handler, which
      // navigates AWAY from the whole declaration
      // (`onEscape: () => navigate(DECLARATIONS_BASE)` in
      // `StudioV2Layout`). Escape here means "close the popover", nothing
      // more.
      e.stopPropagation()
      onCancel()
      return
    }
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      if (!addDisabled) onConfirm()
      return
    }
    // F8 (a11y, review wave 5): a manual trap over the card's own four
    // focusable elements, so Tab/Shift+Tab never leaves the popover for the
    // document behind it (a native modal's own contract; this popover isn't
    // rendered in a `<dialog>`, so nothing gives it that for free).
    if (e.key === "Tab") {
      // A disabled button cannot take focus, so it leaves the cycle while
      // disabled; otherwise Tab onto it would silently keep focus in place.
      const focusable = tabOrder
        .map((ref) => ref.current)
        .filter(
          (el): el is NonNullable<typeof el> =>
            el != null && !(el instanceof HTMLButtonElement && el.disabled),
        )
      const activeIndex = focusable.findIndex(
        (el) => el === document.activeElement,
      )
      if (activeIndex === -1) return
      e.preventDefault()
      const delta = e.shiftKey ? -1 : 1
      const nextIndex =
        (activeIndex + delta + focusable.length) % focusable.length
      focusable[nextIndex].focus()
    }
  }

  return (
    <div
      ref={rootRef}
      data-testid="mark-target-popover"
      className="sv2-markpop"
      style={{ left: position.x, top: position.y }}
      role="dialog"
      aria-modal="true"
      aria-label={translate("MC_DECL_V2_MARK_POPOVER_TITLE")}
      onKeyDown={handleKeyDown}
    >
      <p className="sv2-markpop-quote">{truncateQuote(quote)}</p>
      <textarea
        data-testid="mark-question-textarea"
        ref={textareaRef}
        className="sv2-askta"
        rows={3}
        value={question}
        onChange={(e) => onQuestionChange(e.target.value)}
        placeholder={translate("MC_DECL_V2_MARK_QUESTION_PLACEHOLDER")}
        aria-label={translate("MC_DECL_V2_MARK_QUESTION_PLACEHOLDER")}
      />
      <label className="sv2-seclbl" htmlFor="mark-target-select">
        {translate("MC_DECL_V2_MARK_TARGET_LABEL")}
      </label>
      <select
        id="mark-target-select"
        data-testid="mark-target-select"
        ref={selectRef}
        className="sv2-markpop-select"
        value={`${framework}::${statementId}`}
        onChange={(e) => {
          const [nextFramework, nextStatementId] = e.target.value.split("::")
          onTargetChange(nextFramework, nextStatementId)
        }}
      >
        {targets.map((t) => (
          <option
            key={`${t.framework}::${t.statementId}`}
            value={`${t.framework}::${t.statementId}`}
          >
            {t.label}
          </option>
        ))}
      </select>
      <div className="sv2-markpop-actions">
        <button
          type="button"
          data-testid="mark-cancel-button"
          ref={cancelRef}
          className="sv2-act"
          onClick={onCancel}
        >
          {translate("MC_DECL_V2_MARK_CANCEL")}
        </button>
        <button
          type="button"
          data-testid="mark-add-button"
          ref={confirmRef}
          className="sv2-act sv2-primary"
          disabled={addDisabled}
          onClick={onConfirm}
        >
          {translate("MC_DECL_V2_MARK_ADD")}
        </button>
      </div>
    </div>
  )
}
